import { Link, useNavigate } from "@tanstack/react-router";
import { IconArrowRight, IconSearch } from "@tabler/icons-react";
import {
  Badge,
  Card,
  DatePickerInput,
  DesktopOnly,
  Group,
  Input,
  Loader,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Tr,
  type ColumnaSkeleton,
} from "@/components/ui";
import { formatearMonto } from "@/shared/format/money";
import { formatFecha, formatFechaHora } from "@/shared/format/fecha";
import { parseIso, formatIso } from "@/features/datos-generales/utils/iso-date";
import { etiquetaTipoSolicitud, etiquetaSubtipoSolicitud } from "@/lib/solicitud-labels";
import type { HistorialAutorizacionItem } from "../services/historial-service";
import { badgeEstadoActual } from "./estado-badge";

/** Keys de filtro por columna · espejo de los query params del backend. */
export type HistorialFiltroKey =
  | "folio"
  | "organizacion"
  | "area"
  | "tipo"
  | "proveedor"
  | "numProveedor"
  | "rfcEmisor"
  | "descripcion"
  | "total"
  | "moneda"
  | "nivel"
  | "creacion"
  | "estado"
  | "fechaPago"
  | "autorizadoEl";

export type HistorialFiltros = Partial<Record<HistorialFiltroKey, string>>;

/**
 * Anchos en PORCENTAJE (layout fijo). Mismas columnas de información que la
 * Bandeja de autorización + las propias del historial (nivel autorizado, estado
 * actual, fecha de pago, autorizado el). Fila de filtros por columna bajo los
 * encabezados (server-side · el padre debouncea y manda los query params).
 */
const COLS: {
  key: HistorialFiltroKey;
  label: string;
  w: string;
  align?: "right";
  /** Columnas de fecha filtran por DÍA con un selector de fecha. */
  filtro?: "fecha";
}[] = [
  { key: "folio", label: "Folio", w: "8%" },
  { key: "organizacion", label: "Organización", w: "7.5%" },
  { key: "area", label: "Área", w: "5%" },
  { key: "tipo", label: "Tipo / Subtipo", w: "6.5%" },
  { key: "proveedor", label: "Proveedor", w: "8%" },
  { key: "numProveedor", label: "Núm. Prov", w: "4.5%" },
  { key: "rfcEmisor", label: "RFC Emisor", w: "8%" },
  { key: "descripcion", label: "Descripción", w: "8%" },
  { key: "total", label: "Total CFDI", w: "6%", align: "right" },
  { key: "moneda", label: "Moneda", w: "4%" },
  { key: "nivel", label: "Nivel autorizado", w: "6%" },
  { key: "creacion", label: "Creación", w: "6%", filtro: "fecha" },
  { key: "estado", label: "Estado actual", w: "6%" },
  { key: "fechaPago", label: "Fecha de pago", w: "5.5%", filtro: "fecha" },
  { key: "autorizadoEl", label: "Autorizado el", w: "5.5%", filtro: "fecha" },
];

// Ancho de la columna de acciones ("Ver") · el resto lo reparte el layout fijo.
const ANCHO_ACCIONES = "5.5%";

/** Columnas reales (mismos anchos y alineación) para el skeleton de primera carga. */
export const COLUMNAS_HISTORIAL_SKELETON: ColumnaSkeleton[] = [
  ...COLS.map((c) => ({ pct: c.w, align: c.align })),
  { pct: ANCHO_ACCIONES, align: "right" },
];

export function HistorialDesktopTable({
  items,
  filtros,
  buscando = false,
  onFiltrar,
}: {
  items: HistorialAutorizacionItem[];
  filtros: HistorialFiltros;
  buscando?: boolean;
  onFiltrar: (key: HistorialFiltroKey, valor: string) => void;
}) {
  return (
    <DesktopOnly>
      <Card padding="xs" className="relative p-0">
        {/* Indicador de búsqueda en curso · no bloquea (el usuario puede seguir escribiendo). */}
        {buscando && (
          <Group gap={6} className="pointer-events-none absolute right-3 top-2 z-10">
            <Loader size="xs" color="multiva" />
            <Text size="xs" c="dimmed" fw={500}>
              Buscando…
            </Text>
          </Group>
        )}
        <div className="overflow-x-auto">
          <Table layout="fixed" highlightOnHover verticalSpacing="sm" horizontalSpacing="xs" className="w-full uppercase">
            <Thead>
              <Tr>
                {COLS.map((c) => (
                  <Th key={c.key} align={c.align} style={{ width: c.w, verticalAlign: "top" }}>
                    <Text size="sm" fw={600} className="whitespace-normal break-words leading-tight">
                      {c.label}
                    </Text>
                  </Th>
                ))}
                <Th align="right" style={{ width: ANCHO_ACCIONES }} />
              </Tr>
              <Tr>
                {COLS.map((c) => (
                  <Th key={c.key} className="!p-1">
                    {c.filtro === "fecha" ? (
                      <DatePickerInput
                        size="xs"
                        clearable
                        placeholder="Fecha…"
                        value={parseIso(filtros[c.key])}
                        onChange={(d) => onFiltrar(c.key, formatIso(d))}
                        wrapperClassName="normal-case"
                      />
                    ) : (
                      <Input
                        size="xs"
                        placeholder="Buscar…"
                        leftSection={<IconSearch size={12} stroke={1.6} />}
                        value={filtros[c.key] ?? ""}
                        onChange={(e) => onFiltrar(c.key, e.currentTarget.value)}
                        aria-label={`Filtrar por ${c.label}`}
                        className="normal-case"
                      />
                    )}
                  </Th>
                ))}
                <Th />
              </Tr>
            </Thead>
            <Tbody className="transition-opacity duration-120" style={{ opacity: buscando ? 0.5 : 1 }}>
              {items.length === 0 ? (
                <Tr>
                  <Td colSpan={COLS.length + 1}>
                    <Text size="sm" c="dimmed" ta="center" className="py-3">
                      Sin resultados para los filtros.
                    </Text>
                  </Td>
                </Tr>
              ) : (
                items.map((it) => <HistorialRow key={`${it.solicitudId}-${it.nivelAutorizado}`} it={it} />)
              )}
            </Tbody>
          </Table>
        </div>
      </Card>
    </DesktopOnly>
  );
}

/** Celda de texto truncada a una línea + tooltip (igual que la bandeja). */
function CeldaTexto({ texto, fw }: { texto: string | null | undefined; fw?: 500 }) {
  const val = texto ?? "—";
  if (!texto || texto.length <= 18) {
    return (
      <Text size="sm" truncate fw={fw}>
        {val}
      </Text>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Text size="sm" truncate fw={fw}>
          {val}
        </Text>
      </TooltipTrigger>
      <TooltipContent className="max-w-90">{val}</TooltipContent>
    </Tooltip>
  );
}

function HistorialRow({ it }: { it: HistorialAutorizacionItem }) {
  const navigate = useNavigate();
  const badge = badgeEstadoActual(it.estadoActualSolicitud, {
    nivelAutorizado: it.nivelAutorizado,
    cantidadNiveles: it.cantidadNiveles,
  });
  return (
    <Tr
      className="cursor-pointer"
      onClick={() => navigate({ to: `/solicitudes/${it.solicitudId}` as never })}
    >
      <Td>
        <CeldaTexto texto={it.folio} fw={500} />
      </Td>
      <Td>
        <CeldaTexto texto={it.organizacionNombre} />
      </Td>
      <Td>
        <CeldaTexto texto={it.areaNombre} />
      </Td>
      <Td>
        <Stack gap={0}>
          <Text size="sm" truncate>{etiquetaTipoSolicitud(it.tipo)}</Text>
          <Text size="xs" c="dimmed">{etiquetaSubtipoSolicitud(it.subtipo)}</Text>
        </Stack>
      </Td>
      <Td>
        <CeldaTexto texto={it.proveedorRazonSocial} />
      </Td>
      <Td>
        <Text size="sm">{it.proveedorNumero !== null ? it.proveedorNumero : "—"}</Text>
      </Td>
      <Td>
        <CeldaTexto texto={it.rfcEmisor} />
      </Td>
      <Td>
        <CeldaTexto texto={it.descripcionPrimerConcepto} />
      </Td>
      <Td align="right">
        <Text size="sm" fw={500}>
          {it.totalCfdi !== null ? `$${formatearMonto(it.totalCfdi, null, { minimumFractionDigits: 2 })}` : "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{it.monedaCfdi ?? "—"}</Text>
      </Td>
      <Td>
        <Badge size="sm" color="exito">
          {it.nivelAutorizado}/{it.cantidadNiveles}
        </Badge>
      </Td>
      <Td>
        <Text size="sm">{formatFecha(it.fechaCreacion)}</Text>
      </Td>
      <Td>
        {/* La columna es angosta: el texto del chip se trunca (estados largos como "Baja por Mesa de
            Control") y el tooltip muestra el completo, en vez de desbordarse sobre "Fecha de pago". */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge size="sm" color={badge.color} className="max-w-full">
              <span className="truncate">{badge.label}</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{badge.label}</TooltipContent>
        </Tooltip>
      </Td>
      <Td>
        {it.fechaPago ? (
          <Text size="sm" fw={500}>{formatFecha(it.fechaPago)}</Text>
        ) : (
          <Text size="sm" c="dimmed">—</Text>
        )}
      </Td>
      <Td>
        <Text size="sm" fw={500}>{formatFechaHora(it.autorizadoAt)}</Text>
      </Td>
      <Td align="right" onClick={(e) => e.stopPropagation()}>
        <Link
          to={`/solicitudes/${it.solicitudId}` as never}
          className="inline-flex h-7 items-center gap-1 rounded-md bg-multiva-50 px-2.5 text-xs font-medium text-multiva-800 transition-colors hover:bg-multiva-100 dark:bg-multiva-900/40 dark:text-multiva-200"
        >
          Ver
          <IconArrowRight size={12} />
        </Link>
      </Td>
    </Tr>
  );
}
